// "먼저 볼 학생" 큐. 저장된 결과 필드만으로 정하는 순수 함수라 채점 직후 화면과 기록 화면이 같은 결과를 낸다.
// 규칙(운영 교사 점수 수정 기준 측정): 답안 없음·인식 실패 → 근거 못 찾음 → 짧은 답안 → AI가 한 단계 낮춤.
const getResultTotalScore = (result) => {
  if (Array.isArray(result?.question_results) && result.question_results.length > 0) {
    return result.question_results.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  if (Array.isArray(result?.item_scores) && result.item_scores.length > 0) {
    return result.item_scores.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  return result?.total_score ?? result?.score ?? 0;
};

export const REVIEW_QUEUE_VISIBLE_LIMIT = 8;
export const SHORT_ANSWER_CHARS = 100;
const BLANK_FEEDBACK_SIGNATURE = '채점할 수 있는 본문 내용이 확인되지 않아';

export const REVIEW_REASON_CHIPS = {
  blank: '답안 없음',
  failed: '채점 실패',
  ocr: '글자 인식 실패',
  noEvidence: '근거 못 찾음',
  short: '짧은 답안',
  lowered: '한 단계 낮춤',
};
const REASON_ORDER = ['blank', 'failed', 'ocr', 'noEvidence', 'short', 'lowered'];

const resultItems = (result) => {
  const items = Array.isArray(result?.item_scores) ? [...result.item_scores] : [];
  (Array.isArray(result?.question_results) ? result.question_results : []).forEach((question) => {
    if (Array.isArray(question?.item_scores)) items.push(...question.item_scores);
  });
  return items.filter(item => item && typeof item === 'object');
};

const countAnswerChars = (text) => String(text || '').replace(/\s+/g, '').length;

const answerEntryFor = (answerTexts, index) => {
  if (!Array.isArray(answerTexts)) return null;
  const matched = answerTexts.find(entry => entry && typeof entry === 'object' && Number(entry.result_index) === index);
  const entry = matched || answerTexts[index];
  if (typeof entry === 'string') return { answer_text: entry };
  return entry && typeof entry === 'object' ? entry : null;
};

// 서버는 점수가 실제로 바뀐 항목에만 teacher_score를 남긴다. 근거만 고친 수정 기록으로는 빼지 않는다.
export const isTeacherReviewedResult = (result) => resultItems(result).some(item => item.teacher_score);

const loweredLabel = (items) => {
  const lowered = items.find(item => !item.teacher_score && (String(item.score_cap_reason || '').trim() || item.strict_step_applied));
  if (!lowered) return '';
  if (lowered.grade_only || lowered.score_internal_only) return '수행수준을 한 번 더 확인해 주세요';
  const condition = String(lowered.score_cap_reason || '').match(/'([^']{1,30})'/)?.[1];
  return condition
    ? `'${condition}' 조건이 분명하지 않아 한 단계 낮췄어요`
    : `${lowered.item_name || '평가요소'} 점수를 한 단계 낮췄어요`;
};

// 한 학생의 큐 이유 목록. 이유가 없으면 [].
export const getReviewReasons = (result, answerEntry = null) => {
  if (!result || result.error || isTeacherReviewedResult(result)) return [];
  const items = resultItems(result);
  const reasons = [];
  const source = String(answerEntry?.answer_text_source || '');
  const blank = Boolean(result.is_blank_forced || result.is_empty)
    || String(result.detailed_feedback || '').includes(BLANK_FEEDBACK_SIGNATURE);
  // 스캔 결과의 confidence 'low'는 백지(이미 is_blank_forced)이거나 채점 실패다.
  const failed = !blank && Boolean(result.scoring_failed || result.confidence === 'low');
  if (blank) reasons.push({ key: 'blank', label: '답안이 비어 있어요' });
  if (failed) reasons.push({ key: 'failed', label: 'AI가 채점하지 못했어요. 원본을 보고 점수를 넣어 주세요' });
  if (source === 'scan_ocr_failed') reasons.push({ key: 'ocr', label: '답안 글자를 읽지 못했어요. 원본을 봐 주세요' });
  if (items.some(item => String(item.review_reason || '').trim())) {
    reasons.push({ key: 'noEvidence', label: 'AI가 답안에서 근거 문장을 찾지 못했어요' });
  }
  const answerText = answerEntry?.answer_text ?? result.answer_text;
  if (!blank && !failed && source !== 'scan_ocr_failed' && typeof answerText === 'string') {
    const chars = countAnswerChars(answerText);
    if (chars < SHORT_ANSWER_CHARS) reasons.push({ key: 'short', label: `답안이 짧아요 (${chars}자)` });
  }
  const lowered = loweredLabel(items);
  if (lowered) reasons.push({ key: 'lowered', label: lowered });
  return reasons;
};

const reasonRank = (reasons) => Math.min(...reasons.map(reason => REASON_ORDER.indexOf(reason.key)));

// 결과 목록 → { total, queued: [{ index, reasons }], chipCounts }. queued 는 이유 순위, 같은 순위면 총점 낮은 순.
export const buildReviewQueue = (results, { answerTexts = [] } = {}) => {
  const list = Array.isArray(results) ? results : [];
  const queued = [];
  let total = 0;
  list.forEach((result, index) => {
    if (!result || result.error) return;
    total += 1;
    const reasons = getReviewReasons(result, answerEntryFor(answerTexts, index));
    if (reasons.length) queued.push({ index, reasons, score: Number(getResultTotalScore(result)) || 0 });
  });
  queued.sort((a, b) => (reasonRank(a.reasons) - reasonRank(b.reasons)) || (a.score - b.score) || (a.index - b.index));
  const chipCounts = {};
  queued.forEach(({ reasons }) => {
    const key = REASON_ORDER[reasonRank(reasons)];
    chipCounts[key] = (chipCounts[key] || 0) + 1;
  });
  return { total, queued, chipCounts };
};
